/**
 * @cfleet/customer - InvoiceList Component
 *
 * Displays a paginated list of customer invoices with filtering
 * by status and PDF download functionality.
 *
 * Uses the useCustomerInvoices and useDownloadInvoicePDF hooks
 * from @cfleet/api-client for data fetching.
 */

import React, { useState } from 'react';
import {
  Download,
  Calendar,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  FileText,
  Loader2,
} from 'lucide-react';
import {
  useCustomerInvoices,
  useDownloadInvoicePDF,
  type CustomerInvoice,
  type InvoiceStatus,
} from '@cfleet/api-client';
import { useAuthConfig } from '@/hooks/useAuthConfig';

// ─────────────────────────────────────────────────────────────────────────────
// Status Badge Component
// ─────────────────────────────────────────────────────────────────────────────

const InvoiceStatusBadge: React.FC<{ status: InvoiceStatus }> = ({ status }) => {
  const styles: Record<InvoiceStatus, string> = {
    Paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Open: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    Overdue: 'bg-red-50 text-red-700 border-red-200',
  };

  const icons: Record<InvoiceStatus, typeof CheckCircle2> = {
    Paid: CheckCircle2,
    Open: Clock,
    Overdue: AlertCircle,
  };

  const Icon = icons[status];

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${styles[status]}`}
    >
      <Icon size={12} />
      {status}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Loading Skeleton
// ─────────────────────────────────────────────────────────────────────────────

const InvoiceListSkeleton: React.FC = () => {
  return (
    <div
      data-testid="invoice-list-skeleton"
      className="bg-white"
    >
      {/* Header skeleton */}
      <div className="h-14 border-b border-zinc-200 flex items-center justify-between px-6 bg-white">
        <div className="h-4 w-24 bg-zinc-200 rounded animate-pulse" />
        <div className="flex items-center gap-4">
          <div className="h-8 w-32 bg-zinc-200 rounded animate-pulse" />
          <div className="h-4 w-40 bg-zinc-200 rounded animate-pulse" />
        </div>
      </div>

      {/* Table skeleton */}
      <div className="pb-12">
        <table className="w-full text-left text-sm border-collapse">
          <thead className="sticky top-0 bg-white z-10 border-b border-zinc-200">
            <tr>
              {['Invoice #', 'Status', 'Issued Date', 'Due Date', 'Orders', 'Amount', ''].map(
                (header, i) => (
                  <th key={i} className="px-6 py-3">
                    <div className="h-3 w-20 bg-zinc-200 rounded animate-pulse" />
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {[...Array(5)].map((_, i) => (
              <tr key={i}>
                <td className="px-6 py-3">
                  <div className="h-4 w-32 bg-zinc-100 rounded animate-pulse" />
                </td>
                <td className="px-6 py-3">
                  <div className="h-6 w-16 bg-zinc-100 rounded-full animate-pulse" />
                </td>
                <td className="px-6 py-3">
                  <div className="h-4 w-24 bg-zinc-100 rounded animate-pulse" />
                </td>
                <td className="px-6 py-3">
                  <div className="h-4 w-24 bg-zinc-100 rounded animate-pulse" />
                </td>
                <td className="px-6 py-3">
                  <div className="h-4 w-16 bg-zinc-100 rounded animate-pulse" />
                </td>
                <td className="px-6 py-3">
                  <div className="h-4 w-20 bg-zinc-100 rounded animate-pulse" />
                </td>
                <td className="px-6 py-3">
                  <div className="h-8 w-8 bg-zinc-100 rounded animate-pulse" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Error State Component
// ─────────────────────────────────────────────────────────────────────────────

interface ErrorStateProps {
  message: string;
  onRetry: () => void;
}

const ErrorState: React.FC<ErrorStateProps> = ({ message, onRetry }) => {
  return (
    <div className="flex flex-col items-center justify-center h-full bg-white p-8">
      <div className="text-red-500 mb-4">
        <AlertCircle size={48} />
      </div>
      <h3 className="text-lg font-semibold text-zinc-900 mb-2">Failed to load invoices</h3>
      <p className="text-sm text-zinc-500 mb-4 text-center max-w-md">{message}</p>
      <button
        onClick={onRetry}
        className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-zinc-800 transition-colors"
      >
        <RefreshCw size={16} />
        Retry
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Empty State Component
// ─────────────────────────────────────────────────────────────────────────────

const EmptyState: React.FC = () => {
  return (
    <div className="flex flex-col items-center justify-center h-64 bg-white">
      <div className="text-zinc-300 mb-4">
        <FileText size={48} />
      </div>
      <h3 className="text-lg font-semibold text-zinc-900 mb-2">No invoices</h3>
      <p className="text-sm text-zinc-500 text-center max-w-md">
        You don't have any invoices yet. Invoices will appear here once your orders are billed.
      </p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main InvoiceList Component
// ─────────────────────────────────────────────────────────────────────────────

export const InvoiceList: React.FC = () => {
  const authConfig = useAuthConfig();

  // Pagination and filter state
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | undefined>(undefined);
  const perPage = 20;

  // Fetch invoices
  const {
    data: invoicesData,
    isLoading,
    isError,
    error,
    refetch,
  } = useCustomerInvoices({ page, per_page: perPage, status: statusFilter }, { authConfig });

  // PDF download mutation
  const downloadPDF = useDownloadInvoicePDF({ authConfig });

  const invoices = invoicesData?.data ?? [];
  const pagination = invoicesData?.pagination;
  const summary = invoicesData?.summary;

  // Handle status filter change
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setStatusFilter(value ? (value as InvoiceStatus) : undefined);
    setPage(1); // Reset to first page when filter changes
  };

  // Handle download
  const handleDownload = (invoiceId: string) => {
    downloadPDF.mutate(invoiceId);
  };

  // Loading state
  if (isLoading) {
    return <InvoiceListSkeleton />;
  }

  // Error state
  if (isError) {
    return <ErrorState message={error?.message || 'An error occurred'} onRetry={refetch} />;
  }

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="h-14 border-b border-zinc-200 flex items-center justify-between px-6 bg-white sticky top-0 z-20">
        <h2 className="text-sm font-semibold text-zinc-900">Invoices</h2>
        <div className="flex items-center gap-4">
          {/* Status Filter */}
          <select
            aria-label="Status"
            value={statusFilter || ''}
            onChange={handleStatusChange}
            className="h-8 px-3 text-xs border border-zinc-200 rounded-md bg-white text-zinc-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="Open">Open</option>
            <option value="Paid">Paid</option>
            <option value="Overdue">Overdue</option>
          </select>

          {/* Summary */}
          <div className="text-xs text-zinc-500">
            Total Outstanding:{' '}
            <span className="text-zinc-900 font-mono font-medium ml-1">
              ${summary?.total_outstanding?.toFixed(2) ?? '0.00'}
            </span>
          </div>

          {/* Overdue count */}
          {summary?.overdue_count && summary.overdue_count > 0 ? (
            <span className="text-xs text-red-600 font-medium">
              {summary.overdue_count} overdue
            </span>
          ) : null}
        </div>
      </div>

      {/* Empty state */}
      {invoices.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Table */}
          <div className="pb-12">
            <table className="w-full text-left text-sm border-collapse">
              <thead className="sticky top-14 bg-white z-10 border-b border-zinc-200 text-xs uppercase text-zinc-500 font-medium shadow-sm">
                <tr>
                  <th className="px-6 py-3 font-semibold">Invoice #</th>
                  <th className="px-6 py-3 font-semibold">Status</th>
                  <th className="px-6 py-3 font-semibold">Issued Date</th>
                  <th className="px-6 py-3 font-semibold">Due Date</th>
                  <th className="px-6 py-3 font-semibold">Orders</th>
                  <th className="px-6 py-3 text-right font-semibold">Amount</th>
                  <th className="px-6 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {invoices.map((invoice: CustomerInvoice) => (
                  <tr key={invoice.id} className="group hover:bg-zinc-50 transition-colors">
                    <td className="px-6 py-3 font-mono text-zinc-600 text-xs font-medium">
                      #{invoice.id}
                    </td>
                    <td className="px-6 py-3">
                      <InvoiceStatusBadge status={invoice.status} />
                    </td>
                    <td className="px-6 py-3 text-zinc-600 text-xs">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-zinc-400" />
                        {new Date(invoice.issued_date).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-zinc-600 text-xs">
                      <span
                        className={`${invoice.status === 'Overdue' ? 'text-red-600 font-medium' : ''}`}
                      >
                        {new Date(invoice.due_date).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-zinc-600 text-xs">
                      {invoice.order_count} orders
                    </td>
                    <td className="px-6 py-3 text-right text-zinc-900 font-mono font-medium text-xs">
                      ${invoice.amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <button
                        aria-label="Download"
                        onClick={() => handleDownload(invoice.id)}
                        disabled={downloadPDF.isPending}
                        className="text-zinc-400 hover:text-blue-600 transition-colors p-1.5 hover:bg-blue-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {downloadPDF.isPending ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Download size={16} />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <div className="h-12 border-t border-zinc-200 flex items-center justify-between px-6 bg-white">
              <span className="text-xs text-zinc-500">
                Page {pagination.page} of {pagination.total_pages}
              </span>
              <div className="flex items-center gap-2">
                <button
                  aria-label="Previous"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={!pagination.has_prev}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs border border-zinc-200 rounded-md hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={14} />
                  Previous
                </button>
                <button
                  aria-label="Next"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!pagination.has_next}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs border border-zinc-200 rounded-md hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
